import { useState } from 'react'
import { NUDGGE_API_BASE_URL } from '../../lib/config'

const SMS_FUNCTIONS = [
  {
    name: 'Send-Sms',
    description: 'Send SMS messages via Infobip',
    method: 'POST',
    endpoint: '/functions/v1/Send-Sms',
    features: [
      'Infobip API integration',
      'Multi-tenant account isolation',
      'Delivery tracking via ExternalId',
      'Entry attribute storage',
      'Provider config resolution',
      'Automatic entry logging'
    ],
    businessLogic: [
      { step: 1, title: 'Authentication', description: 'Validates API key from X-Api-Key or Authorization Bearer header' },
      { step: 2, title: 'Account Lookup', description: 'Retrieves SMS account and provider config for the API key' },
      { step: 3, title: 'Provider Resolution', description: 'Loads Infobip API key and base URL from SmsProviderConfigs JsonConfig' },
      { step: 4, title: 'Entry Creation', description: 'Creates SmsEntries record with recipient, body, and account reference' },
      { step: 5, title: 'Attribute Storage', description: 'Stores tracking properties in SmsEntryAttributes' },
      { step: 6, title: 'Infobip API Call', description: 'Sends SMS via Infobip REST API with the configured credentials' },
      { step: 7, title: 'ExternalId Update', description: 'Updates entry with Infobip message ID for delivery tracking' }
    ]
  },
  {
    name: 'Send-Sms-Templated',
    description: 'Send SMS using stored templates with variable interpolation',
    method: 'POST',
    endpoint: '/functions/v1/Send-Sms-Templated',
    features: [
      'Template-based SMS',
      'Variable interpolation',
      'Template UUID lookup',
      'Same provider pipeline as Send-Sms',
      'Tracking properties',
      'Entry audit trail'
    ],
    businessLogic: [
      { step: 1, title: 'Authentication', description: 'Validates API key from request headers' },
      { step: 2, title: 'Template Lookup', description: 'Resolves template by UUID from SmsTemplates table' },
      { step: 3, title: 'Variable Interpolation', description: 'Replaces template variables with provided values' },
      { step: 4, title: 'Account & Provider', description: 'Loads account and Infobip provider configuration' },
      { step: 5, title: 'Entry Creation', description: 'Logs the interpolated message in SmsEntries' },
      { step: 6, title: 'Send via Infobip', description: 'Dispatches SMS through the provider API' },
      { step: 7, title: 'Tracking Update', description: 'Stores ExternalId and tracking attributes' }
    ]
  },
  {
    name: 'Sms-Webhook',
    description: 'Receive delivery status webhooks from Infobip',
    method: 'POST',
    endpoint: '/functions/v1/Sms-Webhook',
    features: [
      'Infobip webhook ingestion',
      'Basic auth verification',
      'Delivery status normalization',
      'Batch event processing',
      'ExternalId matching',
      'Per-event error handling'
    ],
    businessLogic: [
      { step: 1, title: 'Auth Verification', description: 'Validates HTTP Basic Auth credentials against configured secrets' },
      { step: 2, title: 'Payload Parsing', description: 'Extracts delivery report array from Infobip webhook body' },
      { step: 3, title: 'Entry Matching', description: 'Matches each report to SmsEntries via ExternalId (Infobip messageId)' },
      { step: 4, title: 'Event Type Upsert', description: 'Creates or retrieves SmsEventType for the delivery status' },
      { step: 5, title: 'Event Recording', description: 'Inserts SmsEntryDeliveryEvents with status, timestamp, and metadata' },
      { step: 6, title: 'Response', description: 'Returns 202 with per-event success/failure results' }
    ]
  }
]

export default function EdgeFunctionsTab() {
  const [selectedFunction, setSelectedFunction] = useState(null)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex items-center space-x-3 mb-2">
          <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            Edge Functions
          </h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          View and explore SMS-related Supabase Edge Functions
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Function List */}
        <div className="lg:col-span-1 space-y-3">
          {SMS_FUNCTIONS.map((func) => (
            <button
              key={func.name}
              onClick={() => setSelectedFunction(func)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selectedFunction?.name === func.name
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-700'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                      {func.name}
                    </h3>
                    <span className="px-2 py-0.5 text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                      {func.method}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {func.description}
                  </p>
                  <code className="text-xs text-purple-600 dark:text-purple-400 mt-2 block">
                    {func.endpoint}
                  </code>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Function Details */}
        <div className="lg:col-span-2">
          {selectedFunction ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
              {/* Function Header */}
              <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-4 sm:p-6 text-white">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold">{selectedFunction.name}</h3>
                    <p className="text-purple-100 mt-1 text-sm sm:text-base">{selectedFunction.description}</p>
                  </div>
                  <button
                    onClick={() => setSelectedFunction(null)}
                    className="p-1 hover:bg-white/20 rounded-lg transition"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                    {selectedFunction.method}
                  </span>
                  <code className="px-3 py-1 bg-black/20 rounded-full text-sm font-mono">
                    {selectedFunction.endpoint}
                  </code>
                </div>
              </div>

              {/* Features */}
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Key Features
                </h4>
                <ul className="space-y-2">
                  {selectedFunction.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-sm">
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Business Logic Flow */}
              <div className="p-4 sm:p-6">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                  Business Logic Flow
                </h4>
                <div className="space-y-3">
                  {selectedFunction.businessLogic.map((logic, idx) => (
                    <div
                      key={idx}
                      className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {logic.step}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                          {logic.title}
                        </h5>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          {logic.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* API Example */}
              <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  API Example
                </h4>
                <pre className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 overflow-x-auto text-xs">
                  <code className="text-gray-800 dark:text-gray-200">{`curl -X ${selectedFunction.method} \\
  '${NUDGGE_API_BASE_URL}${selectedFunction.endpoint}' \\
  -H 'Content-Type: application/json' \\
  -H 'X-Api-Key: YOUR_API_KEY' \\
  -d '{"to": "+27821234567", "body": "Hello from Nudgge!"}'`}</code>
                </pre>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 sm:p-12 text-center">
              <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Select a Function
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choose an Edge Function from the list to view its implementation and documentation
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
